import { useRef } from 'react';

import StoreService from '../../services/StoreService';
import { Kinesis } from '../../services/TrackerService';
import { ILogFields } from '../../services/TrackerService/Kinesis/interfaces';
import { nothing } from '../yambda';

const IMPRESSION_HISTORY_CACHE_KEY = 'impression_history_cache';
const IMPRESSION_DATA_CACHE_KEY = 'impression_data_cache';

/**
 * @param data 로그 데이터.
 * @param key 로그 데이터의 고유 ID.
 */
export type ImpressionTrackerBuilder<T = Partial<ILogFields>> = (
  data: T,
  key: string | number,
) => (e: IntersectionObserverEntry, unobserve: typeof nothing) => void;

/**
 * 로그 데이터와 고유 ID를 받아서 임프레션을 전송하는 콜백 함수를 반환한다.
 *
 * 반환한 함수를 IntersectionObserver의 onChange에 전달해 사용할 수 있다.
 *
 * @example
 * const trackImpressionWith = useImpressionTracker(data => Kinesis.impression(pageName, data));
 * return (
 *   <IntersectionObserver onChange={trackImpressionWith(logData)}>
 *     <ImpressionTarget data={item} />
 *   </IntersectionObserver>
 * )
 */
export const useImpressionTracker = <T = Partial<ILogFields>>(
  callback: (data: T, e: IntersectionObserverEntry) => void,
): ImpressionTrackerBuilder<T> => {
  const impressionHistory = useRef<Record<string, boolean>>({}).current;

  const trackWith: ImpressionTrackerBuilder<T> = (data, key) => (e, unobserve): void => {
    if (impressionHistory[String(key)] || !e.isIntersecting) {
      return;
    }
    impressionHistory[String(key)] = true;
    callback(data, e);
    unobserve();
  };

  return trackWith;
};

export const impressionLogs: Record<string, Omit<ILogFields, 'eventType' | 'pageName'>> =
  StoreService.getInstance('session').get(IMPRESSION_DATA_CACHE_KEY) || {};

export const sendImpressionLog = (): Promise<void> | void => {
  if (!impressionLogs) {
    return;
  }

  Object.keys(impressionLogs).forEach(listStamp => {
    const logDatas = impressionLogs[listStamp];

    if (!logDatas) {
      return;
    }

    Object.keys(logDatas).forEach(pageName => {
      const logData = logDatas[pageName];
      logData && Kinesis.impression(pageName, logData);
    });
    delete impressionLogs[listStamp];
  });
  // 전송 후 삭제 된것을 업데이트
  StoreService.getInstance('session').set(IMPRESSION_DATA_CACHE_KEY, impressionLogs);
};

export const useImpressionTrackerForList = <
  T extends { objectsWith: any[] } = Partial<ILogFields> & { objectsWith: any[] }
>(
  pageName: string,
  listStamp: number,
  preventDuplicates = true,
): ImpressionTrackerBuilder<T> => {
  const storageInstance = StoreService.getInstance('session');
  const imprHistoryFromCache =
    (preventDuplicates && storageInstance.get(IMPRESSION_HISTORY_CACHE_KEY)?.[String(listStamp)]) || {};
  const impressionHistory = useRef<Record<string, boolean>>(imprHistoryFromCache).current;

  const saveLogHistory = (): void => {
    storageInstance.set(IMPRESSION_HISTORY_CACHE_KEY, {
      [listStamp]: impressionHistory,
    });
    storageInstance.set(IMPRESSION_DATA_CACHE_KEY, impressionLogs);
  };

  return (data, key) => (e, unobserve): void => {
    if (!e.isIntersecting) {
      return;
    }

    if (preventDuplicates) {
      if (impressionHistory[String(key)]) {
        unobserve();
        return;
      }

      impressionHistory[String(key)] = true;
    }

    const impressionLog =
      impressionLogs[listStamp] && impressionLogs[listStamp][pageName] ? impressionLogs[listStamp][pageName] : null;

    // objectsWith 값이 없으면 전송, 있으면 objectsWith을 수집
    if (!data.objectsWith) {
      Kinesis.impression(pageName, data);
    } else {
      // 이미 로그가 있을경우 objectsWith값만 추가 없을경우 통으로 넣는다 (objectsWith값 외엔 다 같을거란 가정)
      impressionLog
        ? impressionLog.objectsWith?.push(data.objectsWith[0])
        : (impressionLogs[listStamp] = { [pageName]: data });

      // 스토리지에 업데이트
      preventDuplicates && saveLogHistory();

      // objectsWith의 값이 100개(impr로그 100개)가 쌓이면 로그 전송 후 새로 수집
      if (impressionLog?.objectsWith && impressionLog.objectsWith.length === 100) {
        Kinesis.impression(pageName, impressionLogs[pageName]);
        impressionLog.objectsWith = [];
      }
    }
    unobserve();
  };
};
